<template>
	<v-row no-gutters class="pa-2 overflow-auto" style="height: 100%">
		<!-- No need to bill the billing page -->

		<v-col cols="12" class="pa-1" style="height: 100%">
			<v-card style="height: 100%; min-height: 300px" :loading="loadingStatus">
				<v-card-title class="py-2 py-lg-4">
					<v-row no-gutters>
						<v-col class="text-left" :class="$vuetify.breakpoint.mdAndDown ? 'font-responsive-md' : ''">
							{{ $t('billings') }}
						</v-col>
						<v-spacer />
						<v-col class="text-right">
							<ActionButton
								icon="mdi-file-delimited"
								tooltip="download-csv"
								:maxSize="25"
								:minSize="20"
								@clicked="downloadCSV()"
								:busy="downloadingIds.includes(-1)" />
						</v-col>
					</v-row>
				</v-card-title>
				<v-card-text
					class="overflow-auto"
					:style="$vuetify.breakpoint.lgAndUp ? 'height: calc(100% - 64px)' : 'height: calc(100% - 48px)'">
					<v-data-table
						:items="groupedBillings"
						item-value="id"
						:headers="headers"
						:items-per-page="-1"
						:height="pageHeight($vuetify.breakpoint.mdAndDown ? 93 : 77)"
						fixed-header
						dense
						sort-by="month"
						sort-desc
						class="data-table-outline overflow-y-auto"
						:footer-props="{ 'items-per-page-text': '' }">
						<template v-slot:item="{ item, expand, isExpanded, index }">
							<tr @click="expand(!isExpanded)" :class="index % 2 ? 'odd-row' : ''">
								<td class="text-left px-1">
									{{ item.description }}
								</td>
								<td class="text-right px-1">
									<ActionButton
										icon="mdi-file-delimited"
										tooltip="download-csv"
										:maxSize="25"
										:minSize="20"
										@clicked="downloadCSV(item)"
										:busy="downloadingIds.includes(item.id)" />
									<v-icon color="primary">
										{{ isExpanded ? 'mdi-menu-up' : 'mdi-menu-down' }}
									</v-icon>
								</td>
							</tr>
						</template>

						<template v-slot:expanded-item="{ headers, item }">
							<td :colspan="headers.length" class="pa-3">
								<v-data-table
									v-if="item.data.length > 0"
									:items="item.data"
									:headers="detailsHeaders"
									:items-per-page="-1"
									style="overflow-y: auto; width: 100%"
									fixed-header
									hide-default-footer
									dense
									class="data-table-outline">
									<template v-slot:item="{ item, index }">
										<tr :class="index % 2 ? 'odd-row' : ''">
											<td class="text-left px-1">{{ item.program }}</td>
											<td class="text-left px-1">{{ item.cptCode }}</td>
											<td class="text-left px-1">{{ item.codeType }}</td>
											<td class="text-left px-1">{{ item.units }}</td>
											<td class="text-left px-1">{{ item.totalTimeSpentReadableFormat }}</td>
											<td class="text-left px-1">
												<v-checkbox class="ma-0 pa-0" v-model="item.isBillable" readonly :ripple="false" hide-details />
											</td>
										</tr>
									</template>
								</v-data-table>
								<div v-else class="text-center grey--text">
									{{ $t('no-history') }}
								</div>
							</td>
						</template>
					</v-data-table>
				</v-card-text>
			</v-card>
		</v-col>
	</v-row>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { getRowsForDataTable, formatDuration, downloadCSVWithKeys, getFullName } from '@/utils/utilities';
import ActionButton from '../ActionButton.vue';

export default {
	name: 'CCMBilling',
	components: { ActionButton },

	data () {
		return {
			loadingStatus: true,
			selectedBilling: {},
			downloadingIds: [],
			groupedBillings: [],
		};
	},

	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
			billings: (state) => state.ccmBilling.ccmBillingByPatientId,
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			diagnoses: (state) => state.ccmDiagnoses.ccmDiagnosesByPatientId,
		}),

		...mapGetters({
			pageHeight: 'pageHeight',
		}),

		headers () {
			return [
				{ text: this.$t('date'), value: 'description', class: 'px-1' },
				{ text: '', value: 'data-table-expand', sortable: false, class: 'px-1' },
			];
		},
		detailsHeaders () {
			return [
				{ text: this.$t('program'), sortable: false, value: 'program', class: 'px-1' },
				{ text: this.$t('cpt-code-header'), sortable: false, value: 'cptCode', class: 'px-1' },
				{ text: this.$t('code-type-header'), sortable: false, value: 'codeType', class: 'px-1' },
				{ text: this.$t('units'), sortable: false, value: 'units', class: 'px-1' },
				{ text: this.$t('time-spent-header'), sortable: false, value: 'totalTimeSpentReadableFormat', class: 'px-1' },
				{ text: this.$t('billable'), sortable: false, value: 'isBillable', class: 'px-1' },
			];
		},
	},

	methods: {
		...mapActions({
			getBillingByPatientId: 'ccmBilling/getCCMBillingByPatientId',
			getBillingById: 'ccmBilling/getCCMBilling',
		}),

		getRowsForDataTable,
		formatDuration,
		downloadCSVWithKeys,
		getFullName,

		groupAndSummarize (data) {
			const monthNames = [
				'',
				'January',
				'February',
				'March',
				'April',
				'May',
				'June',
				'July',
				'August',
				'September',
				'October',
				'November',
				'December',
			];

			const groupedMap = new Map();

			data.forEach((item) => {
				const key = `${item.year}-${String(item.month).padStart(2, '0')}`;

				if (!groupedMap.has(key)) {
					groupedMap.set(key, {
						id: key, // ✅ Add this unique ID
						year: item.year,
						month: item.month,
						description: `${monthNames[item.month]} ${item.year}`,
						totalTimeSeconds: 0,
						data: [],
					});
				}

				const group = groupedMap.get(key);
				group.data.push(item);

				if (item.isBillable) {
					group.totalTimeSeconds += item.totalTimeSeconds;
				}
			});

			return Array.from(groupedMap.values());
		},

		async downloadCSV () {
			this.downloadingIds.push(-1);
			await downloadCSVWithKeys(this.billingsWithDate, 'Billings_for_' + getFullName(this.detailedUser.userData), {
				ids: [],
				data: [
					'program',
					'codeType',
					'cptCode',
					'units',
					'estimatedTimePerUnit',
					'serviceCategory',
					'deviceType',
					'isBillable',
				],
				timestamps: ['billingDate'],
				timeSpent: ['totalTimeSeconds'],
			});
			this.downloadingIds = this.downloadingIds.filter((item) => item !== -1);
		},

		async downloadCSVByMonth (item) {
			this.downloadingIds.push(item.id);
			downloadCSVWithKeys(
				this.billingsWithDate,
				'Billings_on_' + item.year + '-' + item.month + '_for_' + getFullName(this.detailedUser.userData),
				{
					ids: [],
					data: [
						'program',
						'codeType',
						'cptCode',
						'units',
						'estimatedTimePerUnit',
						'serviceCategory',
						'deviceType',
						'isBillable',
					],
					timestamps: [],
					timeSpent: ['totalTimeSeconds'],
				},
			);
			this.downloadingIds = this.downloadingIds.filter((id) => id !== item.id);
		},
	},
	async mounted () {
		if (!this.loadingStatus) {
			this.loadingStatus = false;
		}
		await this.getBillingByPatientId(this.detailedUser.userData.id);
		this.loadingStatus = false;
	},
	watch: {
		billings () {
			this.groupedBillings = this.groupAndSummarize(this.billings);
			const monthNames = [
				'',
				'January',
				'February',
				'March',
				'April',
				'May',
				'June',
				'July',
				'August',
				'September',
				'October',
				'November',
				'December',
			];
			this.billingsWithDate = this.billings.map((item) => ({
				...item,
				billingDate: `${monthNames[item.month]} ${item.year}`,
			}));
		},
	},
};
</script>
